import { Accordion, Typography } from "@hellocarbo/lasagne";
import { TooltipComponentOption } from "echarts/components";
import { TFunction } from "i18next";
import { renderToString } from "react-dom/server";
import { useTranslation } from "react-i18next";

import BarChart, { ECOption } from "@/components/ui/BarChart";
import { Percentiles, TCarbonUnit } from "@/types";
import { formatCarbon } from "@/utils/carbon";
import { getReductionMessage } from "@/utils/review";

import colors from "../../../colors";
import PedagogicLink from "../PedagogicLink";
import ChartTooltip from "../ui/ChartTooltip";

function LimitExcedeedTooltip() {
  const { t } = useTranslation(["review", "formatter"]);

  return (
    <div className="border-grayscale-x-light p-2xs rounded-[8px] border bg-white shadow-lg">
      <Typography step="-1">
        {t("review:comparison.graph-limit-exceeded")}
      </Typography>
    </div>
  );
}

function optionFactory(
  intensity: number,
  percentiles: Array<number>,
  unit: string,
  yAxisMax: number,
  t: TFunction
): ECOption {
  const lastIndex = percentiles.length - 1;
  const pMax = percentiles[lastIndex];

  const currentPercentileIndex =
    intensity >= pMax
      ? lastIndex
      : percentiles.findIndex((value) => intensity < value);

  const bigValuesIndexes = percentiles.reduce(
    (indexes, currentPercentile, currentIndex) => {
      if (currentPercentile > yAxisMax) {
        indexes.push(currentIndex);
      }
      return indexes;
    },
    [] as Array<number>
  );

  return {
    textStyle: {
      fontFamily: "Poppins",
      color: colors["typography"],
    }, // Global text styles
    tooltip: {
      borderWidth: 0,
      padding: 0,
      extraCssText: "border-radius: 8px;",
      formatter: ({ name, value }: { name: string; value: number }) => {
        const { formattedCarbon } = formatCarbon(t, value, TCarbonUnit.tCO2e);

        return renderToString(
          <ChartTooltip
            seriesName={`${name}% ${t("review:comparison.ofCompanies")}`}
            valueToDisplay={formattedCarbon}
            unit={unit}
            iconClassName="size-2xs"
          />
        );
      },
    } as TooltipComponentOption,
    xAxis: {
      type: "category",
      data: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
      axisLine: { show: false }, // Hide x lines
      axisTick: { show: false }, // Hide x small ticks
      axisLabel: {
        margin: 16,
        hideOverlap: false,
        customValues: [0, 4, 8], // Only show the labels for p10, p50 and p90
        formatter: (value, index) => {
          const { formattedCarbon } = formatCarbon(
            t,
            percentiles[index],
            TCarbonUnit.tCO2e
          );
          return (
            "{top|Top " +
            (index + 1) * 10 +
            "%}\n\n" +
            "{carbon|" +
            formattedCarbon +
            "} " +
            "{unit|" +
            unit +
            "}"
          );
        },
        rich: {
          top: {
            backgroundColor: "#FFFF",
            fontSize: 12,
            fontWeight: 500,
            borderWidth: 1,
            borderColor: "#E3E5EE",
            borderRadius: 8,
            padding: 6,
          },
          carbon: {
            fontSize: 14,
            fontWeight: 600,
            padding: [0, 0, 2, 0],
          },
          unit: { color: "#8581AA", fontSize: 10, fontWeight: 500 },
        },
        fontSize: 10,
      },
    },
    yAxis: {
      type: "value",
      axisLine: { show: false }, // Hide y lines
      axisTick: { show: false }, // Hide y small ticks
      axisLabel: { show: false },
      splitLine: { lineStyle: { width: 2, cap: "square", type: [5, 10] } },
      min: 0,
      max: yAxisMax,
    },
    series: [
      {
        z: 5,
        type: "bar",
        data: percentiles,
        barWidth: 24,
        barMinHeight: 5,
        itemStyle: {
          borderRadius: 24,
          color: "#D6DCFF",
        },
        emphasis: {
          itemStyle: {
            color: "#6C63FF",
            shadowOffsetX: 0,
            shadowOffsetY: 1,
            shadowBlur: 8,
            shadowColor: "rgba(59, 46, 129, 0.06)",
          },
        },
        markPoint: {
          z: 6,
          tooltip: {
            formatter: () => renderToString(<LimitExcedeedTooltip />),
          },
          symbolSize: 30,
          symbol:
            "image://data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM2YzYzZmYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBjbGFzcz0ibHVjaWRlIGx1Y2lkZS1hcnJvdy1iaWctdXAiPjxwYXRoIGQ9Ik05IDE4di02SDVsNy03IDcgN2gtNHY2SDl6Ii8+PC9zdmc+",
          data: bigValuesIndexes.map((index) => ({
            coord: [index, yAxisMax],
            name: `big-value-${index}`,
          })),
        },
        markLine: {
          symbolSize: 0,
          silent: true,
          label: {
            formatter: (data) => data.name,
            color: "#FFFF",
            fontWeight: 600,
            backgroundColor: "#6C63FF",
            padding: 6,
            borderRadius: 8,
            distance: [0, 15],
          },
          lineStyle: {
            color: "#6C63FF",
            width: 2,
          },
          data: [
            {
              name: t("review:comparison.you"),
              xAxis: currentPercentileIndex,
            },
          ],
        },
      },
    ],
  };
}

type IntensityContentProps = {
  intensity: number;
  percentiles: Percentiles;
  yourIntensity: string;
  unit: string;
  pedagogicContent: string;
  intensityMax: number;
};

function IntensityContent({
  intensity,
  percentiles,
  yourIntensity,
  unit,
  pedagogicContent,
  intensityMax,
}: IntensityContentProps) {
  const { t } = useTranslation(["review", "formatter"]);

  // Remove p0 and p100
  const data = Object.values(percentiles).slice(1);
  const { formattedCarbon: formattedIntensity } = formatCarbon(
    t,
    intensity,
    TCarbonUnit.tCO2e
  );

  const isPublic = window.location.href.includes("public");

  return (
    <>
      <div className="gap-y-xs pb-lg flex grow flex-col">
        <div className="gap-y-2xs border-grayscale-xx-light p-lg flex flex-col items-center rounded-md border">
          <Typography step="-1" className="text-grayscale">
            {yourIntensity}
          </Typography>
          <Typography step="2" className="gap-x-2xs flex items-end">
            {formattedIntensity}
            <Typography step="-1" className="pb-3xs">
              {unit}
            </Typography>
          </Typography>
        </div>
        <div className="h-[350px]">
          <BarChart
            option={optionFactory(intensity, data, unit, intensityMax, t)}
          />
        </div>
      </div>
      <div className="py-md">
        {getReductionMessage(t, intensity, percentiles, unit)}
      </div>
      <hr className="border-grayscale-x-light" />
      <div className="py-md">
        <Accordion
          items={[
            {
              trigger: t("review:comparison.pedagogicContent.title"),
              content: (
                <div className="gap-y-md flex flex-col">
                  <Typography>{pedagogicContent}</Typography>
                  <div>
                    {!isPublic && (
                      <PedagogicLink
                        href="https://hellocarbo-25361575.hs-sites-eu1.com/knowledge/methodologie-utilisee-pour-la-comparaison"
                        text={t("review:comparison.pedagogicContent.title", {
                          context: "short",
                        })}
                      />
                    )}
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>
    </>
  );
}

export default IntensityContent;
